import { createContext, useContext, useReducer, type ReactNode, type Dispatch } from "react";

import type { ANNOTATION_TYPES, PLACEMENT_TYPE, ANNOTATION_SUBTYPES } from "constants/annotations";
import type { PrepPdfDesignation as Designation } from "common/pdf/interaction/prep/pdf_designation_fragment.graphql";
import type { DocumentBundleMembershipRole } from "graphql_globals";

export const PADDING = 10;
export const MIN_FONT_SIZE = 8;
export const MIN_ANNOTATION_HEIGHT = 10;

export type ToolData = {
  id: string;
  type: ObjectValues<typeof ANNOTATION_TYPES>;
  placementType: ObjectValues<typeof PLACEMENT_TYPE>;
  subtype?: ObjectValues<typeof ANNOTATION_SUBTYPES>;
  userId?: string;
  signerRole?: {
    index: string;
    role: DocumentBundleMembershipRole;
  };
  primaryDesignationId?: string;
  contactInformation?: {
    email?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
  };
  sourceDesignation?: Designation;
};

type PDFState = {
  currentPage: number;
  currentToolData?: ToolData | undefined;
  currentSignerNumber: number;
  currentToolLocked: boolean;
  editingTextField: boolean;
};
type PdfAction =
  | { type: "selectTool"; payload: ToolData | undefined }
  | { type: "resetTools" }
  | { type: "selectCurrentSigner"; payload: number }
  | { type: "lockTool"; payload: boolean }
  | { type: "updatePage"; payload: number }
  | { type: "editingTextField"; payload: boolean };

const pdfReducer = (state: PDFState, action: PdfAction) => {
  switch (action.type) {
    case "selectTool": {
      return { ...state, currentToolData: action.payload };
    }
    case "resetTools": {
      return { ...state, currentToolData: undefined, currentSignerNumber: 1 };
    }
    case "updatePage": {
      return { ...state, currentPage: action.payload };
    }
    case "selectCurrentSigner": {
      return { ...state, currentSignerNumber: action.payload };
    }
    case "lockTool": {
      return { ...state, currentToolLocked: action.payload };
    }
    case "editingTextField": {
      return { ...state, editingTextField: action.payload };
    }
    default:
      return state;
  }
};

const DEFAULT_STATE = {
  currentPage: 0,
  currentSignerNumber: 1,
  currentToolLocked: false,
  editingTextField: false,
};
const PDFContext = createContext<PDFState>(DEFAULT_STATE);
const PDFDispatchContext = createContext<Dispatch<PdfAction> | null>(null);

export function usePdfContext() {
  return useContext(PDFContext);
}
export function usePDFDispatch() {
  return useContext(PDFDispatchContext);
}

export const PDFViewerContext = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(pdfReducer, DEFAULT_STATE);
  return (
    <PDFContext.Provider value={state}>
      <PDFDispatchContext.Provider value={dispatch}>{children}</PDFDispatchContext.Provider>
    </PDFContext.Provider>
  );
};
